import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router-dom'
import { RoutePaths } from '@/navigation/route'
import {
    askAssistant,
    checkAssistantLogsStatus,
    getAssistantConversations,
    sendAssistantFeedback,
} from '@/services/assistant.service'

export type AssistantSource = { title: string; url: string }

export type AssistantExchange = {
    id: number | null
    question: string
    answer?: string
    sources: AssistantSource[]
    liked: boolean | null
    pending?: boolean
    // Automatic continuation (re-sent question once requested logs landed):
    // the question bubble is hidden, only the answer shows
    auto?: boolean
}

export type AssistantThreadSummary = {
    AssistantThreadId: number
    Title: string
    UpdatedAt: string
    messages: any[]
}

type AskInput = {
    question: string
    auto?: boolean
    // Pinned by the continuation flow (the user may have navigated or
    // switched threads while the site box was generating the logs)
    threadId?: number
    siteHash?: string
}

type PendingLogs = {
    threadId: number
    siteHash: string
    start: string
    end: string
    originalQuestion: string
}

const THREAD_STORAGE_KEY = 'agridbot_thread_id'
const LOGS_POLL_INTERVAL_MS = 15000
const LOGS_POLL_MAX_ATTEMPTS = 16 // ~4 minutes, then re-ask anyway (honest failure)

const readStoredThreadId = (): number | null => {
    try {
        const raw = localStorage.getItem(THREAD_STORAGE_KEY)
        const parsed = raw ? parseInt(raw, 10) : NaN
        return Number.isNaN(parsed) ? null : parsed
    } catch {
        return null
    }
}

const mapServerMessage = (message: any): AssistantExchange => ({
    id: message.AssistantThreadMessageId ?? null,
    question: message.Question,
    answer: message.Answer,
    sources: message.Sources || [],
    liked:
        message.Liked === null || message.Liked === undefined ? null : Boolean(message.Liked),
})

export const useAssistant = () => {
    const { i18n } = useTranslation()
    const { pathname } = useLocation()
    // When a site is open (/sites/:siteHash/*), the backend unlocks the
    // site tools (live zones, past logs, confirmed commands) for the bot
    const siteMatch = matchPath(`${RoutePaths.Sites}/:siteHash/*`, pathname)
    const siteHash = siteMatch?.params?.siteHash
    const [threadId, setThreadId] = useState<number | null>(readStoredThreadId)
    const [exchanges, setExchanges] = useState<AssistantExchange[]>([])
    const [historyLoaded, setHistoryLoaded] = useState(false)
    const [conversations, setConversations] = useState<AssistantThreadSummary[] | null>(null)

    // Refs mirrored on every render: the polling timers outlive closures
    const threadIdRef = useRef<number | null>(threadId)
    const askingRef = useRef(false)
    const pendingLogsRef = useRef<PendingLogs | null>(null)
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const persistThreadId = (id: number | null) => {
        setThreadId(id)
        threadIdRef.current = id
        try {
            if (id) localStorage.setItem(THREAD_STORAGE_KEY, String(id))
            else localStorage.removeItem(THREAD_STORAGE_KEY)
        } catch {
            // storage unavailable: the conversation just won't survive a refresh
        }
    }

    const clearLogsPolling = () => {
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
        pendingLogsRef.current = null
    }

    // Cancel any in-flight polling when the widget unmounts (logout…)
    useEffect(() => {
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
        }
    }, [])

    // Reload the active conversation (messages live server-side)
    useEffect(() => {
        const storedThreadId = readStoredThreadId()
        if (!storedThreadId) {
            setHistoryLoaded(true)
            return
        }
        getAssistantConversations()
            .then((res) => {
                setConversations(
                    (res?.threads || []).filter((item: any) => item.messages?.length)
                )
                const thread = res?.threads?.find(
                    (item: any) => item.AssistantThreadId === storedThreadId
                )
                if (!thread) {
                    persistThreadId(null)
                    return
                }
                setExchanges((thread.messages || []).map(mapServerMessage))
            })
            .catch(() => persistThreadId(null))
            .finally(() => setHistoryLoaded(true))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const askMutation = useMutation({
        mutationFn: (input: AskInput) =>
            askAssistant({
                question: input.question,
                threadId: (input.auto ? input.threadId : threadId) ?? undefined,
                language: i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en',
                siteHash: (input.auto ? input.siteHash : siteHash) ?? undefined,
                continuation: input.auto || undefined,
            }),
        onMutate: (input: AskInput) => {
            setExchanges((current) => [
                ...current,
                {
                    id: null,
                    question: input.question,
                    sources: [],
                    liked: null,
                    pending: true,
                    auto: input.auto,
                },
            ])
        },
        onSuccess: (res, input) => {
            if (res?.threadId && !input.auto) persistThreadId(res.threadId)
            setExchanges((current) =>
                current.map((exchange, index) =>
                    index === current.length - 1
                        ? {
                              id: res?.messageId ?? null,
                              question: exchange.question,
                              answer: res?.answer,
                              sources: res?.sources || [],
                              liked: null,
                              auto: exchange.auto,
                          }
                        : exchange
                )
            )
            // The bot asked the site box to generate the missing logs: poll
            // until they land, then automatically re-send the question
            if (!input.auto && res?.pendingLogs?.start && res?.threadId) {
                startLogsPolling({
                    threadId: res.threadId,
                    siteHash: res.pendingLogs.siteHash || siteHash || '',
                    start: res.pendingLogs.start,
                    end: res.pendingLogs.end,
                    originalQuestion: input.question,
                })
            }
        },
        onError: () => {
            // Network-level failure (the API itself answers 200 with a fallback):
            // drop the pending exchange so the user can retry
            setExchanges((current) => current.slice(0, -1))
        },
    })
    askingRef.current = askMutation.isPending

    const fireContinuation = (pending: PendingLogs, attempt = 0) => {
        // The user moved on to another conversation: drop the follow-up (the
        // generated logs stay available if they re-ask later)
        if (threadIdRef.current !== pending.threadId) return
        if (askingRef.current) {
            // A user question is in flight: retry shortly
            pollTimerRef.current = setTimeout(() => fireContinuation(pending, attempt), 2000)
            return
        }
        askMutation.mutate(
            {
                question: pending.originalQuestion,
                auto: true,
                threadId: pending.threadId,
                siteHash: pending.siteHash,
            },
            {
                onError: () => {
                    if (attempt === 0) {
                        pollTimerRef.current = setTimeout(
                            () => fireContinuation(pending, 1),
                            10000
                        )
                    }
                },
            }
        )
    }

    const startLogsPolling = (pending: PendingLogs) => {
        clearLogsPolling()
        if (!pending.siteHash) return
        pendingLogsRef.current = pending
        let attempts = 0
        const poll = async () => {
            if (pendingLogsRef.current !== pending) return // cancelled or replaced
            attempts += 1
            let ready = false
            try {
                const res = await checkAssistantLogsStatus({
                    siteHash: pending.siteHash,
                    start: pending.start,
                    end: pending.end,
                })
                ready = !!res?.ready
            } catch {
                // transient network error: keep polling
            }
            if (pendingLogsRef.current !== pending) return
            if (ready || attempts >= LOGS_POLL_MAX_ATTEMPTS) {
                // Single-shot: disarm before re-asking so the continuation
                // can never re-trigger a polling cycle
                pendingLogsRef.current = null
                pollTimerRef.current = null
                fireContinuation(pending)
                return
            }
            pollTimerRef.current = setTimeout(poll, LOGS_POLL_INTERVAL_MS)
        }
        pollTimerRef.current = setTimeout(poll, LOGS_POLL_INTERVAL_MS)
    }

    const ask = (question: string) => {
        const trimmed = question.trim()
        if (!trimmed || askMutation.isPending) return
        askMutation.mutate({ question: trimmed })
    }

    const newConversation = () => {
        clearLogsPolling()
        persistThreadId(null)
        setExchanges([])
        askMutation.reset()
    }

    const loadConversations = async () => {
        try {
            const res = await getAssistantConversations()
            setConversations((res?.threads || []).filter((item: any) => item.messages?.length))
        } catch {
            setConversations([])
        }
    }

    const openThread = (thread: AssistantThreadSummary) => {
        clearLogsPolling()
        askMutation.reset()
        persistThreadId(thread.AssistantThreadId)
        setExchanges((thread.messages || []).map(mapServerMessage))
    }

    const feedback = (messageId: number, liked: boolean) => {
        setExchanges((current) =>
            current.map((exchange) =>
                exchange.id === messageId
                    ? { ...exchange, liked: exchange.liked === liked ? null : liked }
                    : exchange
            )
        )
        const exchange = exchanges.find((item) => item.id === messageId)
        const nextLiked = exchange?.liked === liked ? null : liked
        sendAssistantFeedback({ messageId, liked: nextLiked }).catch(() => undefined)
    }

    return {
        exchanges,
        historyLoaded,
        ask,
        asking: askMutation.isPending,
        askFailed: askMutation.isError,
        newConversation,
        conversations,
        loadConversations,
        openThread,
        threadId,
        feedback,
    }
}
